// The back-office session: who is signed in (GET /api/v1/platform/me) and the CSRF token the
// fetcher sends back on writes. A tiny vanilla store read with useSyncExternalStore, so the
// back-office needs no customer-session code (separate realm, separate cookie).
import { PlatformMeSchema, type PlatformMe } from '@batiflow/contracts';

export type AdminSessionStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface AdminSessionState {
  status: AdminSessionStatus;
  me: PlatformMe | null;
}

export interface AdminSessionStore {
  getState: () => AdminSessionState;
  subscribe: (listener: () => void) => () => void;
  /** Loads /platform/me; a 401 (resolved null by `loadMe`) means "not signed in". */
  bootstrap: () => Promise<void>;
  /** Reloads /platform/me without going back to the loading state (keeps the page on screen). */
  refresh: () => Promise<void>;
  /** Forgets the session (after sign-out or a 401). */
  clear: () => void;
}

export interface AdminSessionOptions {
  /** Loads GET /api/v1/platform/me; resolves null when nobody is signed in (401). */
  loadMe: () => Promise<unknown>;
}

export function createAdminSessionStore({ loadMe }: AdminSessionOptions): AdminSessionStore {
  let state: AdminSessionState = { status: 'idle', me: null };
  const listeners = new Set<() => void>();
  const set = (next: AdminSessionState) => {
    state = next;
    for (const listener of listeners) listener();
  };

  const load = async (quiet: boolean) => {
    if (!quiet) set({ status: 'loading', me: null });
    try {
      const raw = await loadMe();
      if (raw === null) {
        set({ status: 'anonymous', me: null });
        return;
      }
      set({ status: 'authenticated', me: PlatformMeSchema.parse(raw) });
    } catch (error) {
      if (quiet) throw error;
      set({ status: 'error', me: null });
    }
  };

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    bootstrap: () => load(false),
    refresh: () => load(true),
    clear: () => {
      set({ status: 'anonymous', me: null });
    },
  };
}

/** The CSRF token to send on writes (null before sign-in). */
export const selectCsrfToken = (state: AdminSessionState): string | null =>
  state.me?.csrfToken ?? null;
